import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { backgroundImg } from "src/assets/assetsExport";

interface CareersPage2Props {}

export default function CareersPage2(props: CareersPage2Props): JSX.Element {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const toggleJob = (jobId: string) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  return (
    <>
      <Helmet>
        <title>Careers | Talent Acquisition</title>
        <meta
          name="description"
          content="Join the Engine. Are you passionate about the luxury property market? Letting Engine is always looking for talent to drive our vision forward."
        />
        <meta
          name="keywords"
          content="careers, jobs, property management, london, real estate, letting agent"
        />
      </Helmet>

      {/* 1. HERO */}
      <section className="min-h-[90vh] pt-40 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.meeting2}
          alt="meeting"
          className="absolute inset-0 w-full h-full object-cover opacity-15 filter grayscale contrast-125 pointer-events-none"
          style={{ zIndex: 0 }}
        />
        <div className="relative z-10 text-center max-w-4xl px-6">
          <h1 className="font-teko text-6xl leading-none mb-8 tracking-[-2px]">
            <span className="stroke-1 text-text-slate">
              Join Our Team <br />
              at Letting Engine
            </span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-text-slate max-w-2xl mx-auto mb-12">
            Are you passionate about the luxury property market? Do you thrive
            in a dynamic and client-focused environment? Letting Engine, a
            leading residential property letting agency in London, is always
            looking for talented individuals to join our team. If you are
            driven, detail-oriented, and dedicated to providing exceptional
            service, we invite you to explore career opportunities with us.
          </p>
          <div className="flex justify-center flex-col sm:flex-row gap-8 sm:gap-16  border-t border-t-solid border-t-[rgba(0,0,0,0.1)] pt-8">
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">London</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Prime HQ
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">Hybrid</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Flexible Working
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">Talent</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Always Hiring
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CULTURE: WHY WORK WITH US */}
      <section className="py-32 px-[5%] relative bg-bg-white pr-10">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-20 pl-12 border-l-4 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs uppercase tracking-widest text-text-slate block mb-2">
              Our Culture
            </span>
            <h2 className="font-teko text-6xl text-text-obsidian">
              Why Join Us?
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-12 pt-8">
            <div className="pl-16 border-l border-black/10 relative">
              <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                01
              </span>
              <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                Growth
              </h3>
              <p className="text-[#555] leading-relaxed">
                We foster a supportive culture where every team member is
                valued. Professional development is not just encouraged; it's
                engineered into your role.
              </p>
            </div>

            <div className="pl-16 border-l border-black/10 relative">
              <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                02
              </span>
              <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                Excellence
              </h3>
              <p className="text-[#555] leading-relaxed">
                Join a team committed to innovation. We set the highest
                standards in the London property industry, refusing to settle
                for 'good enough'.
              </p>
            </div>
            <div className="pl-16 border-l border-black/10 relative">
              <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                03
              </span>
              <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                Collaboration
              </h3>
              <p className="text-[#555] leading-relaxed">
                We believe success is collective. Our open-plan, flat-hierarchy
                environment ensures your voice is heard and your ideas impact
                the bottom line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CURRENT OPENINGS */}
      <section className="py-32 px-[5%] relative bg-bg-porcelain ">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-20 pl-8 border-l-4 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs uppercase tracking-widest text-text-slate block mb-2">
              Vacancies
            </span>
            <h2 className="font-teko text-6xl text-text-obsidian">
              Active Roles
            </h2>
          </div>

          {/* Job 1 */}
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] mb-8 transition-all duration-400 ease-tech relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_98%,98%_100%,0_100%)]">
            <button
              type="button"
              onClick={() => toggleJob("job1")}
              className={`w-full p-8 md:px-12 flex-col lg:flex-row lg:items-center items-start gap-4 cursor-pointer flex justify-between  bg-[var(--glass-surface)] border-b border-b-solid border-b-[rgba(0,0,0,0.05)] transition-all duration-300  ${
                expandedJob === "job1" ? "bg-text-obsidian text-white" : ""
              }`}
            >
              <h3
                className={`font-teko text-4xl text-start ${
                  expandedJob === "job1" ? "text-white" : "text-text-obsidian"
                }`}
              >
                Property Management Executive
              </h3>
              <div className="flex gap-4 md:gap-8 items-center lg:flex-wrap">
                <span
                  className={`font-manrope text-xs uppercase tracking-wide bg-bg-porcelain px-4 py-1 border border-solid border-[#ddd] font-semibold ${
                    expandedJob === "job1"
                      ? "bg-text-obsidian text-white"
                      : "bg-bg-porcelain text-text-obsidian"
                  }`}
                >
                  London, HQ
                </span>
                <span
                  className={`font-manrope text-xs uppercase tracking-wide bg-bg-porcelain px-4 py-1 border border-solid border-[#ddd] font-semibold ${
                    expandedJob === "job1"
                      ? "bg-text-obsidian text-white"
                      : "bg-bg-porcelain text-text-obsidian"
                  }`}
                >
                  {" "}
                  Full Time
                </span>
                <div
                  className={`text-3xl transition-all duration-300 ${
                    expandedJob === "job1"
                      ? "rotate-45 text-white"
                      : "text-brand-yellow"
                  }`}
                >
                  +
                </div>
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-600 ease-in-out ${
                expandedJob === "job1"
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-12 py-12 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 ">
                {/* Col 1: Responsibilities */}
                <div>
                  <h4 className="font-teko text-2xl mb-6">Responsibilities</h4>
                  <ul className="list-none p-0">
                    {[
                      "Conduct property viewings and showcase luxury rental properties to potential tenants",
                      "Negotiate contract terms and conditions in the best interest of both landlords and tenants",
                      "Prepare rental agreements and other necessary documentation",
                      "Provide exceptional customer service and build strong relationships with clients",
                      "Manage a portfolio of luxury rental properties, ensuring efficient operations and maintenance",
                      "Coordinate property inspections, repairs, and maintenance tasks",
                      "Respond promptly to tenant inquiries, concerns, and maintenance requests",
                      "Conduct regular property visits to ensure compliance with tenancy agreements",
                      "Coordinate move-in and move-out processes",
                      "Oversee rent collection, financial reporting, and renting renewals",
                      "Keep accurate records and maintain property management CRM system",
                      "Handle administrative tasks such as scheduling, filing, and data entry",
                      "Stay updated on market trends, property values, and rental regulations",
                    ].map((item, index) => (
                      <li
                        key={index}
                        className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed"
                      >
                        <div className="absolute left-0 top-2.5 w-1.5 h-1.5 bg-brand-yellow transform skew-x-[var(--skew)]"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Col 2: Requirements */}
                <div>
                  <h4 className="font-teko text-2xl mb-6">Requirements</h4>
                  <ul className="list-none p-0">
                    {[
                      "Minimum of a Bachelor's degree",
                      "Experience in property management, preferably in the luxury rental market",
                      "Strong communication and negotiation skills",
                      "Good knowledge of the London property market",
                      "Proven ability to build and maintain client relationships",
                      "Excellent organizational and time management abilities",
                      "Strong organizational and problem-solving skills",
                      "Member of ARLA is a plus",
                      "Proficiency in MRI property management tool is a plus",
                    ].map((item, index) => (
                      <li
                        key={index}
                        className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed"
                      >
                        <div className="absolute left-0 top-2.5 w-1.5 h-1.5 bg-text-obsidian transform skew-x-[var(--skew)]"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Placeholder for future jobs */}
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] mb-8 transition-all duration-400 ease-tech relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_98%,98%_100%,0_100%)] opacity-50 pointer-events-none">
            <div className="w-full p-8 md:px-12 flex-col lg:flex-row lg:items-center items-start gap-4 cursor-pointer flex justify-between  bg-[var(--glass-surface)] border-b border-b-solid border-b-[rgba(0,0,0,0.05)] transition-all duration-300">
              <h3 className="font-teko text-4xl text-text-obsidian">
                Senior Negotiator
              </h3>
              <div className="flex gap-8 items-center lg:flex-wrap">
                <span className="font-manrope text-xs uppercase tracking-wide bg-bg-porcelain px-4 py-1 border border-solid border-[#ddd] font-semibold">
                  Closed
                </span>
              </div>
            </div>
          </div>
          {/* Additional closed placeholder */}
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] mb-8 transition-all duration-400 ease-tech relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_98%,98%_100%,0_100%)] opacity-50 pointer-events-none">
            <div className="w-full p-8 md:px-12 flex-col lg:flex-row lg:items-center items-start gap-4 cursor-pointer flex justify-between  bg-[var(--glass-surface)] border-b border-b-solid border-b-[rgba(0,0,0,0.05)] transition-all duration-300">
              <h3 className="font-teko text-4xl text-text-obsidian">
                Lettings Administrator
              </h3>
              <div className="flex gap-8 items-center lg:flex-wrap">
                <span className="font-manrope text-xs uppercase tracking-wide bg-bg-porcelain px-4 py-1 border border-solid border-[#ddd] font-semibold">
                  Closed
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. HOW TO APPLY */}
      <section className="py-32 px-[5%] relative bg-[#1a1a1a] text-white text-center">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-16 pl-8 border-l-4 border-l-solid border-l-[rgba(255,255,255,0.2)] w-fit mx-auto">
            <span className="font-manrope text-xs uppercase tracking-widest text-[rgba(255,255,255,0.5)] block mb-2">
              Protocol
            </span>
            <h2 className="font-teko text-6xl text-white">
              Initiate Application
            </h2>
          </div>
          <div className="inline-block bg-[rgba(255,255,255,0.05)] border border-solid border-[rgba(255,255,255,0.1)] p-8 mobile:p-12 md:px-20 md:transform md:skew-x-[var(--skew)] backdrop-blur-md">
            <div className="md:transform md:skew-x-[10deg]">
              <a
                href="mailto:hr@lettingengine.com"
                className="font-teko text-5xl text-brand-yellow no-underline border-b-2 border-b-solid border-b-transparent transition-all duration-300 hover:border-b-brand-yellow hover:text-white"
              >
                hr@lettingengine.com
              </a>
              <p className="text-[#888] font-manrope text-sm mt-4 max-w-[400px] mx-auto leading-relaxed">
                To apply, please send your resume and a cover letter. Specify
                the position in the subject line. Only shortlisted candidates
                will be contacted.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
